import { RequestTaskModel } from ".";
import { LatLngModel } from "../../Shared/LatLngModel";

export type RequestModel = LatLngModel & {
  council: string;
  requestId: string;
  requestSerialId: string;
  requestType: string;
  councilReferenceNumber: string;
  addressName: string;
  addressUnitNo: string;
  addressStreetNo: string;
  addressStreetNoDisplay: string;
  addressStreetName: string;
  addressSuburbName: string;
  addressStateCode: string;
  addressPostCode: string;
  addressCountry: string;
  requestedAt: Date | string;
  completedAt: Date | string;
  status: string;
  premiseType: string;
  serviceType: string;
  existingBinsAtAddress: string;
  tasks: RequestTaskModel[];
  action: number;
  isCommercial: boolean;
  assessmentNumber: string;
  requestNotes: string;
};

export enum RequestStatus {
  Pending = "PENDING",
  InProgress = "IN_PROGRESS",
  InProgressAssigned = "IN_PROGRESS_ASSIGNED_TO_TRUCK",
  InProgressWithErrors = "IN_PROGRESS_WITH_ERRORS",
  Complete = "COMPLETE",
  CompleteWithErrors = "COMPLETE_WITH_ERRORS",
  Cancelled = "CANCELLED"
}

export function isTaskComplete(task: RequestTaskModel) {
  return (
    task.status
      .toString()
      .toUpperCase()
      .includes("UNSUCCESSFUL") ||
    task.status.toString().toUpperCase() == "REVIEW" ||
    task.status.toString().toUpperCase() == "SUCCESSFUL" ||
    task.status === "DONE SUCCESSFULLY" ||
    task.status === "DONE BUT UNSUCCESSFUL"
  );
}

export function isRequestComplete(request: RequestModel) {
  return (
    request.status
      .toString()
      .toUpperCase()
      .includes("UNSUCCESSFUL") ||
    request.status.toString().toUpperCase() == "REVIEW" ||
    request.status.toString().toUpperCase() == "SUCCESSFUL" ||
    request.status === "DONE SUCCESSFULLY" ||
    request.status === "DONE BUT UNSUCCESSFUL" ||
    request.status === "CANCELLED"
  );
}

export function isTaskPending(task: RequestTaskModel) {
  return task.status.toString().toUpperCase() == "PENDING";
}

export function isRequestPending(request: RequestModel) {
  return request.status.toString().toUpperCase() == "PENDING";
}

export function isTaskUnsuccessful(task: RequestTaskModel) {
  return task.status
    .toString()
    .toUpperCase()
    .includes("UNSUCCESSFUL");
}

export function isTaskCancelled(task: RequestTaskModel) {
  return task.status
    .toString()
    .toUpperCase()
    .includes("CANCELLED");
}

export function isRequestCancelled(request: RequestModel) {
  return request.status.toString().toUpperCase() == "CANCELLED";
}

export function completedTaskCount(item: RequestModel) {
  if (!item.tasks) return 0;
  return item.tasks.filter(isTaskComplete).length;
}

export function pendingTaskCount(item: RequestModel) {
  if (!item.tasks) return 0;
  return item.tasks.filter(isTaskPending).length;
}

export function unsuccessfulTaskCount(item: RequestModel) {
  if (!item.tasks) return 0;
  return item.tasks.filter(isTaskUnsuccessful).length;
}

export function hasUnsuccessfulTasks(item: RequestModel) {
  return unsuccessfulTaskCount(item) > 0;
}

export function isComplete(item: RequestModel) {
  if (item.tasks.length === 0) {
    return isRequestComplete(item);
  }
  // If all tasks are cancelled, it's not complete (it's cancelled)
  if (item.tasks.every(isTaskCancelled)) {
    return false;
  }
  // Complete if all tasks are either complete or cancelled
  return item.tasks.every(task => isTaskComplete(task) || isTaskCancelled(task));
}

export function isPending(item: RequestModel) {
  return item.tasks.length > 0 ? pendingTaskCount(item) === item.tasks.length : isRequestPending(item);
}

export function isCancelled(item: RequestModel) {
  return item.tasks.length > 0 ? item.tasks.every(isTaskCancelled) : isRequestCancelled(item);
}

export function getStatus(request: RequestModel) {
  // Pending takes highest priority
  if (isPending(request)) {
    return RequestStatus.Pending;
  }

  // If all tasks are cancelled (or request is cancelled and has no tasks)
  if (isCancelled(request)) {
    return RequestStatus.Cancelled;
  }

  // Complete/CompleteWithErrors
  const complete = isComplete(request);
  const hasErrors = hasUnsuccessfulTasks(request);
  if (complete && !hasErrors) {
    return RequestStatus.Complete;
  }
  if (complete && hasErrors) {
    return RequestStatus.CompleteWithErrors;
  }

  // InProgressWithErrors
  if (!complete && hasErrors) {
    return RequestStatus.InProgressWithErrors;
  }

  // InProgressAssigned or InProgress
  if (!complete && !hasErrors) {
    if (request.tasks.some(task => !!task.truckCode)) {
      return RequestStatus.InProgressAssigned;
    }
    return RequestStatus.InProgress;
  }

  throw "Unable to get request status";
}
